import React from 'react';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';

import { ApButtonSecondary } from '@alixpartners/ui-components';
import { createServerSideDatasource } from './graphQLDataSource';
import {
  IDataSourceProps,
  ITaxonomyItem,
  ITransactionItem,
  IRuleItem,
  ClassificationModalEditor,
  SpendTypeEnum,
} from '..';
import { setLocalStorageItem, getLocalStorageItem } from 'utils/localStorage';
import { CellClickedEvent } from 'ag-grid-community';
import { CHILDREN_KEY, GROUP_ID_KEY, RULE_COLUMNS, RULE_ID } from './metadata';

import { LastUpload } from '../LastUpload';

import {
  ActionsWrapper,
  ActionItem,
  Wrapper,
  Title,
  RightSection,
  Divider,
} from './ClassificationTable.styles';

const WRAPPER_THEME_STYLES = {
  height: '100%',
  width: '100vw',
};
const LOCAL_STORAGE_SUFFIX = '-GRID-COLUMNS';

interface IState {
  undos: any[];
  isSaving: boolean;
  editorParams: null | CellClickedEvent;
}

interface IProps {
  gridConfig: AgGridReactProps['gridOptions'];
  columnParams: IDataSourceProps;
  spendType: SpendTypeEnum;
  label: string;
  timestamp: string | null;
  taxonomyOptions: ITaxonomyItem[];
  onBack?(): void;
  updateTransactions(data: ITransactionItem[]): Promise<any>;
  updateRules(data: IRuleItem[]): Promise<any>;
  fetchSpendDataInfo(): Promise<any>;
  hasWritePermission: boolean;
}

export class ClassificationTable extends React.Component<IProps, IState> {
  state = {
    undos: [] as IState['undos'],
    isSaving: false,
    editorParams: null as IState['editorParams'],
  };

  gridApi = null as AgGridReactProps['api'];
  gridColumnApi = null as AgGridReactProps['columnApi'];

  getGridOptions = () => {
    const { columnParams, gridConfig } = this.props;
    const { groupColumnKey, groupHeader, childrenColumns } = columnParams;

    return {
      defaultColDef: {
        filter: 'agTextColumnFilter',
        sortable: true,
        resizable: true,
        floatingFilter: true,
      },
      cacheBlockSize: 50,
      maxBlocksInCache: 10,
      rowModelType: 'serverSide',
      serverSideStoreType: 'partial',
      maxConcurrentDatasourceRequests: 1,
      rowSelection: 'multiple',
      treeData: true,
      autoGroupColumnDef: {
        field: groupColumnKey,
        headerName: groupHeader,
        lockPosition: true,
        cellRendererParams: {
          innerRenderer: function (params: any) {
            return `${params.data[groupColumnKey]}${
              params.data[CHILDREN_KEY]
                ? ` (${params.data[CHILDREN_KEY].length})`
                : ''
            }`;
          },
        },
      },
      animateRows: true,
      serverSideSortingAlwaysResets: true,
      sideBar: {
        toolPanels: [
          {
            id: 'columns',
            labelDefault: 'Columns',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
              suppressRowGroups: true,
              suppressValues: true,
              suppressPivotMode: true,
            },
          },
          'filters',
        ],
      },
      getRowNodeId: (data: any) => {
        return data[GROUP_ID_KEY] || data[childrenColumns.id];
      },
      rowClassRules: {
        'unclassified-row-group-start': (row: any) => {
          if (this.gridApi) {
            const currentIndex = row.rowIndex;

            return (
              this.hasUnclassifiedTransactions(row) &&
              !this.hasUnclassifiedTransactions(
                this.gridApi.getDisplayedRowAtIndex(currentIndex - 1)
              )
            );
          }

          return false;
        },

        'unclassified-row-group': (row: any) => {
          return this.hasUnclassifiedTransactions(row);
        },

        'unclassified-row-group-end': (row: any) => {
          if (this.gridApi) {
            return (
              this.hasUnclassifiedTransactions(row) &&
              !this.hasUnclassifiedTransactions(
                this.gridApi.getDisplayedRowAtIndex(row.rowIndex + 1)
              )
            );
          }

          return false;
        },
      },
      onGridReady: this.onGridReady,
      onCellFocused: this.handleCellFocus,
      onCellKeyDown: this.onCellKeyDown,
      isServerSideGroup: this.getGroupKey,
      getServerSideGroupKey: this.getGroupKey,
      onCellDoubleClicked: this.handleCellDoubleClicked,
      onColumnMoved: this.handleColumnsChange,
      onColumnResized: this.handleColumnsChange,
      onColumnVisible: this.handleColumnsChange,
      onColumnPinned: this.handleColumnsChange,
      onColumnValueChanged: this.handleColumnsChange,
      onSortChanged: this.handleColumnsChange,
      onModelUpdated: this.onModelUpdated,
      onRowClicked: this.onRowClicked,
      ...gridConfig,
    };
  };

  getGroupKey = (dataItem: any) => dataItem[GROUP_ID_KEY];

  onModelUpdated = () => {
    if (this.gridApi) {
      this.gridApi.redrawRows();
    }
  };

  undo = (shouldNotSave?: boolean) => {
    const { undos } = this.state;
    const lastActionData = undos.slice(-1)[0];
    const remainingUndos = undos.slice(0, -1);
    const isRules = !!lastActionData[0][GROUP_ID_KEY];

    // Update created rule ids if initially was not defined
    if (isRules) {
      lastActionData.forEach((row: any) => {
        const currentRowState = this.gridApi?.getRowNode(row[GROUP_ID_KEY]);
        const createdRuleId = currentRowState?.data[RULE_ID];

        if (createdRuleId) {
          row[RULE_ID] = createdRuleId;
        }
      });
    } else {
      this.setDataToRows(lastActionData);
    }

    this.setState({ undos: remainingUndos });

    if (!shouldNotSave) {
      this.updateClassification(
        this.getUpdateDataFromRows(lastActionData)
      ).then(() => {
        if (!isRules) {
          const { childrenColumns } = this.props.columnParams;
          const currentRow = this.gridApi?.getRowNode(
            lastActionData[0][childrenColumns.id]
          );
          const parentNode = currentRow?.parent;

          if (parentNode) {
            this.setParentChildren(parentNode, lastActionData);
          }
        }
      });
    }
  };

  hasUnclassifiedTransactions = (row: any) => {
    if (!row || !row.data || !row.data[CHILDREN_KEY]) {
      return false;
    }

    return (
      !row.data[RULE_COLUMNS.categoryL2Code] &&
      !row.data[CHILDREN_KEY].every((item: any) => {
        return !!item[RULE_COLUMNS.categoryL2Code];
      })
    );
  };

  setDataToRows = (data: any) => {
    if (this.gridApi) {
      data.forEach((rowData: any) => {
        const rowNode = this.gridApi!.getRowNode(
          rowData[GROUP_ID_KEY] || rowData.id
        );
        const flashColumns = [] as any;

        if (rowNode) {
          Object.keys(rowNode.data).forEach((key) => {
            if (rowNode.data[key] !== rowData[key]) {
              flashColumns.push(key);
            }
          });

          this.gridApi?.flashCells({
            rowNodes: [rowNode],
            columns: flashColumns,
          });
          rowNode.setData(rowData);
        } else {
          this.gridApi?.purgeServerSideCache();
        }
      });
    }
  };

  handleCellFocus = (e: any) => {
    if (e && e.api) {
      const selectedNodes = e.api.getSelectedNodes();
      const selectedNodeList = selectedNodes.map((i: any) => i.rowIndex);
      const focusedRow = e.api.getDisplayedRowAtIndex(e.rowIndex);

      // Controll parent/children multiselect selection
      // Make sure you are able to multiselect either parents or children
      if (
        selectedNodes.length > 0 &&
        selectedNodes[0].data &&
        focusedRow.data &&
        !!selectedNodes[0].data[GROUP_ID_KEY] !==
          !!focusedRow.data[GROUP_ID_KEY]
      ) {
        e.api.deselectAll();
      }

      if (selectedNodeList.includes(e.rowIndex)) {
        e.api.gridOptionsWrapper.gridOptions.suppressRowClickSelection = true;
      } else {
        e.api.gridOptionsWrapper.gridOptions.suppressRowClickSelection = false;
      }
    }
  };

  onRowClicked = (e: any) => {
    //Fix for multiple select of a large number of rows
    if (e && e.api && e.event) {
      const selectedNodes = e.api.getSelectedNodes();

      if (
        selectedNodes.length > 0 &&
        e.event.shiftKey &&
        selectedNodes[0].data &&
        e.data &&
        !!selectedNodes[0].data[GROUP_ID_KEY] &&
        !!e.data[GROUP_ID_KEY]
      ) {
        const focusIndex = e.rowIndex;
        const firstIndex = selectedNodes[0].rowIndex;
        const lastIndex = selectedNodes[selectedNodes.length - 1].rowIndex;

        if (focusIndex >= firstIndex && focusIndex <= lastIndex) {
          return;
        }

        let start = null as null | number;
        let end = null as null | number;

        if (focusIndex < firstIndex) {
          start = focusIndex;
          end = firstIndex;
        } else if (focusIndex > lastIndex) {
          start = lastIndex;
          end = focusIndex;
        }

        e.api.forEachNode((node: any) => {
          if (node.rowIndex >= start! && node.rowIndex <= end!) {
            node.setSelected(true);
          }
        });
      }
    }
  };

  onCellKeyDown = ({ event }: any) => {
    if (event.key.toLowerCase() !== 'z') {
      return;
    }

    const { undos } = this.state;

    if (
      undos.length > 0 &&
      ((navigator.appVersion.indexOf('Mac') !== -1 && event.metaKey) ||
        (navigator.appVersion.indexOf('Mac') === -1 && event.ctrlKey))
    ) {
      this.undo();
    }
  };

  getUndoTitle = () => {
    const metaKeyName =
      navigator.appVersion.indexOf('Mac') !== -1 ? '⌘Cmd' : 'Ctrl';

    return `Press ${metaKeyName}+Z to undo your last action`;
  };

  handleCellDoubleClicked = (cellEvent: CellClickedEvent) => {
    const { hasWritePermission } = this.props;

    if (!hasWritePermission) {
      return;
    }

    if (
      cellEvent?.colDef?.field === RULE_COLUMNS.apSpendCategoryL1 ||
      cellEvent?.colDef?.field === RULE_COLUMNS.apSpendCategoryL2
    ) {
      this.setState({ editorParams: cellEvent });
    }
  };

  closeEditor = () => {
    this.setState({ editorParams: null });
  };

  updateClassification = (data: any) => {
    const { updateTransactions, updateRules, fetchSpendDataInfo } = this.props;
    const isRules = data[0][RULE_ID] !== undefined;
    const updateRequest = !isRules ? updateTransactions : updateRules;
    this.setState({ isSaving: true });

    return updateRequest(data).then((rsp) => {
      this.setState({ isSaving: false });
      fetchSpendDataInfo();

      if (isRules && this.gridApi) {
        this.gridApi.purgeServerSideCache();
      }

      return rsp;
    });
  };

  getUpdateDataFromRows = (rows: any[]) => {
    const isRules = !!rows[0][GROUP_ID_KEY];
    const { childrenColumns } = this.props.columnParams;

    if (!isRules) {
      return rows.map((item) => ({
        [childrenColumns.id]: item[childrenColumns.id],
        [RULE_COLUMNS.apSpendCategoryL1]: item[RULE_COLUMNS.apSpendCategoryL1],
        [RULE_COLUMNS.apSpendCategoryL2]: item[RULE_COLUMNS.apSpendCategoryL2],
        [RULE_COLUMNS.categoryL1Code]: item[RULE_COLUMNS.categoryL1Code],
        [RULE_COLUMNS.categoryL2Code]: item[RULE_COLUMNS.categoryL2Code],
      }));
    }

    return rows.map((item) => {
      const parentValues = this.getGroupColumns().reduce(
        (acc: any, columnKey) => {
          return { ...acc, [columnKey]: item[columnKey] };
        },
        {}
      );

      return {
        [RULE_COLUMNS.apSpendCategoryL1]: item[RULE_COLUMNS.apSpendCategoryL1],
        [RULE_COLUMNS.apSpendCategoryL2]: item[RULE_COLUMNS.apSpendCategoryL2],
        [RULE_COLUMNS.categoryL1Code]: item[RULE_COLUMNS.categoryL1Code],
        [RULE_COLUMNS.categoryL2Code]: item[RULE_COLUMNS.categoryL2Code],
        [RULE_ID]: item[RULE_ID],
        ...parentValues,
      };
    });
  };

  handleEditorSubmit = (data: ITaxonomyItem) => {
    if (!this.gridApi) {
      return;
    }

    const selectedRowNodes = this.gridApi.getSelectedNodes();

    if (
      selectedRowNodes.length === 1 &&
      selectedRowNodes[0].data[RULE_COLUMNS.apSpendCategoryL1] ===
        data.apL1Category &&
      selectedRowNodes[0].data[RULE_COLUMNS.apSpendCategoryL2] ===
        data.apL2Category
    ) {
      return;
    }

    const { undos } = this.state;
    const updatedProps = {
      [RULE_COLUMNS.apSpendCategoryL1]: data.apL1Category,
      [RULE_COLUMNS.apSpendCategoryL2]: data.apL2Category,
      [RULE_COLUMNS.categoryL1Code]: data.l1Code,
      [RULE_COLUMNS.categoryL2Code]: data.l2Code,
    };
    let undoData = [] as IState['undos'];

    const updateData = selectedRowNodes.map((rowNode) => {
      const updatedRow = { ...rowNode.data, ...updatedProps };

      undoData = [...undoData, rowNode.data];

      if (!rowNode.data[GROUP_ID_KEY]) {
        rowNode.setData(updatedRow);
      }

      return updatedRow;
    });

    if (undoData.length > 0) {
      this.setState({ undos: [...undos, undoData] });
    }

    this.updateClassification(this.getUpdateDataFromRows(updateData))
      .then(() => {
        const isRules = !!updateData[0][GROUP_ID_KEY];

        this.closeEditor();

        if (!isRules && selectedRowNodes && selectedRowNodes[0]) {
          const parentNode = selectedRowNodes[0].parent;
          this.setParentChildren(parentNode, updateData);
        }
      })
      .catch(() => {
        this.setState({ isSaving: false });
        this.undo(true);
      });
  };

  setParentChildren = (parentNode: any, updateData: any) => {
    const { childrenColumns } = this.props.columnParams;
    const updatedChildren = parentNode?.data[CHILDREN_KEY];

    updateData.forEach((dataItem: any) => {
      const parentItemIndex = updatedChildren.findIndex(
        (item: any) => item[childrenColumns.id] === dataItem[childrenColumns.id]
      );
      if (parentItemIndex !== -1) {
        updatedChildren[parentItemIndex] = dataItem;
      }
    });

    parentNode?.setData({
      ...parentNode.data,
      [CHILDREN_KEY]: updatedChildren,
    });
  };

  getGroupColumns = () => {
    const { parentColumns, childrenColumns } = this.props.columnParams;

    return Object.values(parentColumns).filter((columnKey) => {
      return !Object.values(childrenColumns).includes(columnKey);
    });
  };

  getEditorProps = () => {
    const { editorParams } = this.state;

    if (!editorParams) {
      return { isRule: false };
    }

    const l1 = editorParams.data[RULE_COLUMNS.apSpendCategoryL1];
    const l2 = editorParams.data[RULE_COLUMNS.apSpendCategoryL2];
    const isRule = !!editorParams && !!editorParams.data[GROUP_ID_KEY];

    return {
      ...(l1 && l2
        ? {
            value: {
              apL1Category: l1,
              apL2Category: l2,
            },
          }
        : {}),
      isRule,
    };
  };

  setStateFromLocalStorage = () => {
    const { spendType, gridConfig } = this.props;
    const colState =
      getLocalStorageItem(spendType + LOCAL_STORAGE_SUFFIX) ||
      gridConfig?.columnDefs;

    if (
      this.gridColumnApi &&
      colState !== null &&
      this.gridColumnApi !== null
    ) {
      this.gridColumnApi.setColumnState(colState);
    }
  };

  handleColumnsChange = ({ columnApi }: any) => {
    if (!columnApi) {
      return;
    }

    if (this.gridApi) {
      this.gridApi.redrawRows();
    }

    const { spendType } = this.props;
    const columnState = columnApi.getColumnState();

    setLocalStorageItem(spendType + LOCAL_STORAGE_SUFFIX, columnState);
  };

  onGridReady = (params: any) => {
    const { columnParams } = this.props;
    const datasource = createServerSideDatasource(columnParams);

    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    this.gridApi?.setServerSideDatasource(datasource);
    this.setStateFromLocalStorage();
  };

  render() {
    const {
      label,
      timestamp,
      taxonomyOptions,
      spendType,
      onBack,
      hasWritePermission,
    } = this.props;
    const { undos, isSaving, editorParams } = this.state;
    const isUndoDisabled = isSaving || undos.length === 0;
    const gridOptions = this.getGridOptions();

    return (
      <Wrapper>
        <ActionsWrapper>
          <Title>{label} spend classification</Title>
          {onBack && (
            <ApButtonSecondary iconName="baseline_arrow_back" onClick={onBack}>
              Back to data upload
            </ApButtonSecondary>
          )}

          {hasWritePermission && (
            <>
              <Divider />
              <ActionItem
                title={!isUndoDisabled ? this.getUndoTitle() : undefined}
              >
                <ApButtonSecondary
                  iconName="baseline_undo"
                  disabled={isUndoDisabled}
                  onClick={() => this.undo()}
                >
                  Undo
                </ApButtonSecondary>
              </ActionItem>
            </>
          )}

          {timestamp && (
            <RightSection>
              <LastUpload timestamp={timestamp} />
            </RightSection>
          )}
        </ActionsWrapper>
        <div style={WRAPPER_THEME_STYLES} className="ag-theme-balham">
          <AgGridReact {...gridOptions} />
        </div>
        {!!editorParams && (
          <ClassificationModalEditor
            isOpen={!!editorParams}
            onClose={this.closeEditor}
            onSubmit={this.handleEditorSubmit}
            options={taxonomyOptions}
            isDisabled={isSaving}
            spendType={spendType}
            {...this.getEditorProps()}
          />
        )}
      </Wrapper>
    );
  }
}
